import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';

export const addImageElement = (pdf: jsPDF, canvas: HTMLCanvasElement, pdfHeaderRef: any) => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight() - 10;

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  const ratio = pdfWidth / canvasWidth;
  const pageHeightInCanvas = pdfHeight / ratio;

  let remaining = canvasHeight;
  let srcY = 0;
  let position = pdfHeaderRef.current || 0;

  while (remaining > 0) {
    const sliceHeight = Math.min(pageHeightInCanvas, remaining);
    if (sliceHeight <= 0) break;

    const segmentCanvas = document.createElement('canvas');
    segmentCanvas.width = canvasWidth;
    segmentCanvas.height = sliceHeight;

    const ctx = segmentCanvas.getContext('2d');
    if (!ctx) break;

    ctx.drawImage(canvas, 0, srcY, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight);
    const imgData = segmentCanvas.toDataURL('image/jpeg', 0.92);

    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, sliceHeight * ratio);
    position += sliceHeight * ratio;

    if (position + 1 > pdfHeight) {
      pdf.addPage();
      position = 0;
    }

    remaining -= sliceHeight;
    srcY += sliceHeight;
  }

  pdfHeaderRef.current = 0;
};

type ExportPdfParams = {
  selector: string;
  fileName: string;
  scale?: number;
  ignoreClass?: string;
  beforeExport?: () => void;
  afterExport?: () => void;
  addImageElement: (pdf: jsPDF, canvas: HTMLCanvasElement, pdfHeaderRef: any) => void;
};

export const exportPdf = async ({
  selector,
  fileName,
  scale = 2,
  ignoreClass = 'export-hide',
  beforeExport,
  afterExport,
  addImageElement
}: ExportPdfParams) => {
  try {
    beforeExport?.();

    await document.fonts.ready;
    await new Promise((r) => setTimeout(r, 3000));

    const container = document.querySelector(selector);
    const elements = container?.querySelectorAll('.pdf-page');

    if (!elements || elements.length === 0) return;

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: 'a4'
    });

    let pdfHeaderRef = { current: 0 };

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLElement;

      const originalDPR = window.devicePixelRatio;
      const originalWidth = el.style.width;
      el.style.width = el.scrollWidth + 'px';

      Object.defineProperty(window, 'devicePixelRatio', {
        get: () => 1,
        configurable: true
      });

      const canvas = await html2canvas(el, {
        scale,
        useCORS: true,
        windowHeight: el.scrollHeight,
        ignoreElements: (el) => el.classList?.contains(ignoreClass)
      });

      Object.defineProperty(window, 'devicePixelRatio', {
        get: () => originalDPR
      });
      el.style.width = originalWidth;

      if (i !== 0) {
        pdf.addPage();
        pdfHeaderRef.current = 0;
      }

      addImageElement(pdf, canvas, pdfHeaderRef);
    }

    pdf.save(`${fileName}.pdf`);
  } finally {
    afterExport?.();
  }
};
